import { RecipeStep } from 'components/RecipeStep';
import { TWContainer } from 'components/TWContainer';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import type { DictionaryItem } from 'service/dictionary/dictionaryService';
import { DictionaryService } from 'service/dictionary/dictionaryService';
import { TrackingService } from 'service/tracking/trackingService';
import styled from 'styled-components';
import type { RecipeResult } from 'types/Recipes';

const IngredientsAndEquipmentBlock = dynamic(
  () =>
    import('components/IngredientsAndEquipment').then((m: any) => m.IngredientsAndEquipmentBlock),
  { ssr: true },
) as any;

const RecipeHint = dynamic(() => import('components/RecipeHint').then((m: any) => m.RecipeHint), {
  ssr: true,
}) as any;

const RecipeTimeOverview = dynamic(
  () => import('components/RecipeTimeOverview').then((m: any) => m.RecipeTimeOverview),
  { ssr: true },
) as any;

const StencilDownload = dynamic(
  () => import('components/StencilComponent').then((m: any) => m.StencilDownload),
  { ssr: true },
) as any;

const SwitchComponent = dynamic(
  () => import('components/ToggleSwitch').then((m: any) => m.SwitchComponent),
  { ssr: true },
) as any;

const RecipeTips = dynamic(() => import('components/RecipeTips').then((m) => m.RecipeTips), {
  ssr: true,
});

const RecipeProducts = dynamic(
  () => import('components/RecipeProducts').then((m: any) => m.RecipeProducts),
  { ssr: true },
) as any;

const Title = dynamic(() => import('components/Typography').then((m: any) => m.Title), {
  ssr: true,
}) as any;

const Text = dynamic(() => import('components/Typography').then((m: any) => m.Text), {
  ssr: true,
}) as any;

const Grid = dynamic(() => import('components/Grid').then((m: any) => m.Grid), {
  ssr: true,
}) as any;

const GridItem = dynamic(() => import('components/Grid').then((m: any) => m.GridItem), {
  ssr: true,
}) as any;

const GridRow = dynamic(() => import('components/Grid').then((m: any) => m.GridRow), {
  ssr: true,
}) as any;

const SeparatorLineTips = styled.div`
  margin-top: 20px;
  margin-bottom: 20px;
  border-top: 1px solid #bbc4ba;
`;
const TipsContainer = styled.div`
  width: 946px;
  margin: auto;
  margin-top: 8rem;
  display: none;
  position: absolute;
  bottom: 0;
`;
const TipsHeading = styled.div`
  margin-bottom: 20px;
`;
const SingleTip = styled.div``;

const Container = styled.div`
  position: relative;
  margin-bottom: 8rem;
`;

const LineContainer = styled.div`
  padding-left: 3px;
  padding-right: 3px;
`;
const LightGreyLine = styled.div`
  border: 1px solid #e1b87f;
  margin-top: 5rem;
  margin-bottom: 5rem;
  width: 381px;
  @media (max-width: 767px) {
    width: 100%;
  }
`;

const Svg = styled.svg`
  position: absolute;
  height: 6rem;
  width: 100%;
  top: -5rem;
`;

const SvgBottom = styled.svg`
  bottom: 0;
  position: absolute;
`;

const Ingredients = styled.div<{ $isActive: boolean }>`
  display: ${(props) => (props.$isActive ? 'block' : 'none')};
  @media (min-width: 768px) {
    display: block;
    position: sticky;
    top: 80px;
    max-height: calc(100vh - 80px);
  }
`;

const IngredientsInner = styled.div`
  @media (min-width: 768px) {
    max-height: calc(100vh - 80px);
    overflow-y: auto;
    display: block;
    padding-right: 17px;
    box-sizing: content-box;
  }
`;

const Preparation = styled.div<{ $isActive: boolean }>`
  display: ${(props) => (props.$isActive ? 'block' : 'none')};
  @media (min-width: 768px) {
    display: block;
    max-height: calc(100vh - 80px);
    overflow-y: auto;
  }
`;

const TabContainer = styled.div`
  position: sticky;
  top: 60px;
  z-index: 9;
  padding-top: 1rem;
  padding-bottom: 0.063rem;
  background: #fdf9f4;
  @media (min-width: 768px) {
    position: relative;
    top: auto;
    padding-top: 0;
    padding-bottom: 0;
  }
`;

// TODO move this to component library
const Tabs = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  padding-top: 20px;
  @media (min-width: 768px) {
    display: none;
  }
`;
// TODO move this to component library
const Tab: any = styled(Title as any)<{ $isActive: boolean }>`
  width: 50%;
  text-align: center;
  font-size: 1.125rem;
  color: #8d9c8c;
  font-family: var(--font-primary), Arial;
  line-height: 1.5rem;
  color: ${(props) => (props.$isActive ? '#292B29' : '#E1B87F')};
`;
// TODO move this to component library
const TabsLine = styled.div<{ $currentTab: string }>`
  display: flex;
  margin: 0.563rem 1.375rem 0.75rem;
  height: 3px;
  background-color: #efdbb2;
  @media (min-width: 768px) {
    display: none;
    margin: 0.75rem;
  }
  justify-content: ${(props) => (props.$currentTab === 'ingredients' ? 'flex-start' : 'flex-end')};
`;

const Line = styled.div`
  height: 3px;
  width: 50%;
  background-color: #292b29;
`;

const SeparatorLine = styled.div`
  border-top: 1px solid #bbc4ba;
`;

const RecipeDetail = ({
  recipe,
  copy,
  tipslifeStyle,
  upperRightImage,
  ratingData,
  ratingMsg,
  settingRateData,
  recipeTitle,
  showCommentField,
  showContactPageData,
  contactPageData,
  showRatingBanner,
  hideTabsOnMobile = false,
  currentUrl,
}: {
  recipe: RecipeResult;
  copy: DictionaryItem[];
  tipslifeStyle: Record<string, string>;
  upperRightImage: Record<string, string>;
  ratingData: any;
  ratingMsg: any;
  settingRateData: any;
  recipeTitle: string;
  showCommentField: boolean;
  showContactPageData: boolean;
  contactPageData: string;
  showRatingBanner: boolean;
  hideTabsOnMobile: boolean;
  currentUrl: string;
}) => {
  const router = useRouter();
  const routeKey = router.asPath;
  const [activeTab, setActiveTab] = useState<string>('ingredients');
  const recipeTimeOverviewTitles = {
    total: DictionaryService.getCopy('total', copy) || 'total',
    prep: DictionaryService.getCopy('prep', copy) || 'preparation',
    bake: DictionaryService.getCopy('bake', copy) || 'bake',
    resting: DictionaryService.getCopy('resting', copy) || 'resting',
    minutes: DictionaryService.getCopy('minutes', copy) || 'min',
  };
  const recipeTipsTitle = DictionaryService.getCopy('recipe_tips', copy);
  const toggleSwitchLabel = DictionaryService.getCopy('toggle_switch_label', copy);
  const watchVideoLabel = DictionaryService.getCopy('watch_video', copy);

  const recipeTimeOverviewData = {
    total: recipe?.TotalTimeInMinutes ?? 0,
    prep: recipe?.PreparationTimeInMinutes ?? 0,
    bake: recipe?.BakingTimeInMinutes ?? 0,
    resting: recipe?.RestingTimeInMinutes ?? 0,
  };
  const RecipeTipsData = recipe?.ClassicInstruction?.Tips;
  const preparationBlocks = recipe?.ClassicInstruction?.PreparationBlocks;
  const containerStyle = `${!RecipeTipsData?.length ? 'mb-40' : ''}`;
  const [showAssets, setShowAssets] = useState(false);
  const [showSwitchComponent, setShowSwitchComponent] = useState(false);
  const tabsRef = useRef(null);
  const ingredientsRef = useRef(null);
  const preparationRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (preparationBlocks && Array.isArray(preparationBlocks)) {
      preparationBlocks.forEach((element: any) => {
        if ((element?.Images?.length ?? 0) > 0 || (element?.Videos?.length ?? 0) > 0) {
          setShowSwitchComponent(true);
        }
      });
    }
  }, [preparationBlocks]);

  const scrollTabsTop = (ref: any) => {
    ref.current.scrollIntoView(true);
  };
  const executeTabsScroll = () => scrollTabsTop(tabsRef);
  useEffect(() => {
    if (activeTab !== '') {
      executeTabsScroll();
    }
  }, [activeTab]);

  const scrollIngredientsTop = (ref: any) => {
    const ingredientsTop = ref.current?.getBoundingClientRect().top;
    const viewHeight = window.innerHeight;
    if (ingredientsTop > viewHeight || ingredientsTop < -viewHeight) {
      ref.current.scrollTo(0, 0);
    }
  };

  const executeIngredientsScroll = () => scrollIngredientsTop(ingredientsRef);

  useEffect(() => {
    window.scrollTo(0, 0);
    window.addEventListener('scroll', executeIngredientsScroll, true);
    return () => {
      window.removeEventListener('scroll', executeIngredientsScroll, true);
    };
  }, []);

  const ref: any = useRef(0);

  const printTipsHeading: any = copy?.filter((item) => item.key === 'tips_heading_print_page')?.[0]
    ?.value;

  // Observe preparation-blocks
  useEffect(() => {
    const preparation = preparationRef.current;

    if (!preparation) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            ref.current = 1;
            TrackingService.recipePreparation(recipe.Title);
          }
        });
      },
      {
        rootMargin: '0px',
        threshold: 0.1,
      },
    );

    observer.observe(preparation);

    return () => observer.disconnect();
  }, [routeKey, recipe.Title]);

  const data = RecipeTipsData?.map((tip: any) => ({
    Title: tip.Title,
    Text: tip.Text,
  }));

  return (
    <>
      <TWContainer
        twClasses="relative inline-block w-full bg-light-yellow md:mt-14"
        data-test="recipe-details"
      >
        <Svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 624 28" data-print="hide">
          <path
            fill="#FDF9F4"
            d="M16 203C7.16344 203 0 195.837 0 187V21.351C0 21.351 143.433 -1.4836e-05 313.3 0C483.167 1.48361e-05 624 21.3511 624 21.3511V187C624 195.837 616.836 203 608 203H16Z"
          />
        </Svg>
        <Container className={containerStyle} ref={tabsRef}>
          <div id="inner-ingredients-tab-container">
            {!hideTabsOnMobile ? (
              <TabContainer data-print="hide">
                <Tabs data-test="tabs">
                  <Tab
                    $isActive={activeTab === 'ingredients'}
                    onClick={() => {
                      setActiveTab('ingredients');
                    }}
                    tag="div"
                    type="xxxs"
                    data-test="tab"
                    role="button"
                  >
                    {DictionaryService.getCopy('ingredients', copy)}
                  </Tab>
                  <TWContainer twClasses="text-xxs text-green-500 leading-6">
                    <svg
                      width="4"
                      height="5"
                      viewBox="0 0 4 5"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M2 0.5625C0.929688 0.5625 0.0625 1.42969 0.0625 2.5C0.0625 3.57031 0.929688 4.4375 2 4.4375C3.07031 4.4375 3.9375 3.57031 3.9375 2.5C3.9375 1.42969 3.07031 0.5625 2 0.5625Z"
                        fill="#E1B87F"
                      />
                    </svg>
                  </TWContainer>
                  <Tab
                    $isActive={activeTab === 'preparation'}
                    onClick={() => {
                      setActiveTab('preparation');
                    }}
                    tag="div"
                    type="xxxs"
                    data-test="tab"
                  >
                    {DictionaryService.getCopy('preparation', copy)}
                  </Tab>
                </Tabs>
                <TabsLine $currentTab={activeTab}>
                  <Line />
                </TabsLine>
              </TabContainer>
            ) : null}
            <Grid>
              <GridRow columns={16} className="print-columns-16">
                <GridItem colSpan={8} className="print-col-span-8">
                  <Ingredients
                    $isActive={hideTabsOnMobile || activeTab === 'ingredients'}
                    data-test="ingredients"
                    data-print="show"
                  >
                    <IngredientsInner ref={ingredientsRef} data-print="show">
                      {recipe && (
                        <>
                          <IngredientsAndEquipmentBlock
                            block={recipe}
                            copy={copy}
                            trackingEvent={TrackingService.clickProductLinkRecipe}
                            hideTabsOnMobile={hideTabsOnMobile}
                          />
                          {recipe.ClassicInstruction && <RecipeHint recipe={recipe} copy={copy} />}
                        </>
                      )}
                      {recipe?.Stencils?.[0] && (
                        <>
                          <LineContainer>
                            <LightGreyLine />
                          </LineContainer>
                          <StencilDownload stencilData={recipe.Stencils} copy={copy} />
                        </>
                      )}
                    </IngredientsInner>
                  </Ingredients>
                </GridItem>
                <GridItem colSpan={8} className="print-col-span-8">
                  <Preparation
                    $isActive={hideTabsOnMobile || activeTab === 'preparation'}
                    data-test="preparation"
                    data-print="show"
                    className="recipeInstructions"
                    tabIndex={0}
                  >
                    <div
                      className={hideTabsOnMobile ? '' : 'hidden md:block'}
                      data-test="title"
                      data-print="show"
                    >
                      <Title tag="h2" type="md">
                        {DictionaryService.getCopy('preparation', copy)}:
                      </Title>
                    </div>
                    <RecipeTimeOverview
                      data={recipeTimeOverviewData}
                      titles={recipeTimeOverviewTitles}
                    />
                    {showSwitchComponent && (
                      <>
                        <SeparatorLine id="video-switch-component" data-print="hide" />
                        <SwitchComponent
                          setShowAssets={setShowAssets}
                          showAssets={showAssets}
                          toggleSwitchLabel={toggleSwitchLabel}
                        />
                      </>
                    )}
                    {preparationBlocks &&
                      Array.isArray(preparationBlocks) &&
                      preparationBlocks.length && (
                        <div className="preparation-blocks" ref={preparationRef}>
                          <RecipeStep
                            currentUrl={currentUrl}
                            steps={preparationBlocks}
                            trackStep={TrackingService.recipeStep}
                            showAssets={showAssets}
                            dictionary={copy}
                            showRatingBanner={showRatingBanner}
                            ratingData={ratingData}
                            ratingMsg={ratingMsg}
                            settingRateData={settingRateData}
                            recipeTitle={recipeTitle}
                            showCommentField={showCommentField}
                            showContactPageData={showContactPageData}
                            contactPageData={contactPageData}
                          />
                        </div>
                      )}
                    {hideTabsOnMobile ? (
                      <div className="md:hidden mt-8">
                        <RecipeProducts
                          block={recipe}
                          copy={copy}
                          trackingEvent={TrackingService.clickProductLinkRecipe}
                        />
                      </div>
                    ) : null}
                  </Preparation>
                </GridItem>
              </GridRow>
            </Grid>
          </div>
        </Container>
        <Container data-print="hide">
          {RecipeTipsData?.length ? (
            <RecipeTips
              data={RecipeTipsData}
              recipeTipsTitle={recipeTipsTitle}
              backgroundAsset={tipslifeStyle}
              upperRightCorner={upperRightImage}
              watchVideoLabel={watchVideoLabel}
            />
          ) : (
            <SvgBottom xmlns="http://www.w3.org/2000/svg" viewBox="0 0 624 28">
              <path
                fill="#ffffff"
                d="M16 203C7.16344 203 0 195.837 0 187V21.351C0 21.351 143.433 -1.4836e-05 313.3 0C483.167 1.48361e-05 624 21.3511 624 21.3511V187C624 195.837 616.836 203 608 203H16Z"
              />
            </SvgBottom>
          )}
        </Container>
      </TWContainer>
      {data && data.length > 0 && (
        <TipsContainer id="print-tips-wrapper" data-print="hide">
          <TipsHeading>
            <Title tag="div" type="lg">
              {printTipsHeading}
            </Title>
          </TipsHeading>
          <>
            {data.map((tip: any, index: number) => (
              <SingleTip key={tip.Title ? `${tip.Title}-${index}` : `tip-${index}`}>
                <Text tag="div" type="lg">
                  <strong>{tip.Title}</strong>: {tip.Text}
                </Text>

                {index < data.length - 1 && <SeparatorLineTips />}
              </SingleTip>
            ))}
          </>
        </TipsContainer>
      )}
    </>
  );
};

export default RecipeDetail;
